// `CookieConnectMethod` — US-178 cookie-paste pairing surface.
//
// Two stages:
//   1. **Validate** — paste an `li_at` cookie, click "Validate cookie",
//      hit `POST /linkedin-accounts/validate-cookie`. The API resolves the
//      profile via Voyager `/me` without persisting anything.
//   2. **Confirm + connect** — show the resolved identity (avatar, name,
//      headline, vanitySlug) and a "Confirmer et lier le compte" CTA that
//      fires `POST /linkedin-accounts` with `{ name, vanitySlug, …, liAt }`.
//
// The cleartext `li_at` is held in component state for the validate→commit
// window only — it never leaves the React tree before the explicit confirm
// click and is wiped from state on success/cancel.

import { Lock, ShieldCheck } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';

import {
  ApiError,
  useCreateLinkedinAccount,
  useValidateCookie,
  type LinkedinAccountRow,
  type ValidateCookieResponse,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Avatar, Banner, Button, Card, Textarea } from '@barel/ui';

export interface CookieConnectMethodProps {
  /** Called once an account has been created server-side. */
  onConnected: (account: LinkedinAccountRow) => void;
}

type Stage = 'paste' | 'preview';

const ERROR_KEY_BY_API_CODE: Record<string, string> = {
  'account.cookie_invalid': 'connect.cookie.error.unauthorized',
  'validation.failed': 'connect.cookie.error.invalid_format',
  'auth.unauthorized': 'connect.cookie.error.unauthorized',
  'plan.account_quota': 'connect.cookie.error.server',
};

function mapApiError(err: unknown): string | null {
  if (!ApiError.is(err)) return 'connect.cookie.error.network';
  const mapped = ERROR_KEY_BY_API_CODE[err.code];
  if (mapped) return mapped;
  if (err.httpStatus === 401 || err.httpStatus === 403) {
    return 'connect.cookie.error.unauthorized';
  }
  if (err.httpStatus >= 500) return 'connect.cookie.error.server';
  if (err.httpStatus === 0 || err.code === 'internal_error') {
    return 'connect.cookie.error.network';
  }
  return 'connect.cookie.error.server';
}

/** Cheap shape check before round-tripping to the API. */
function looksLikeLiAt(value: string): boolean {
  const trimmed = value.trim();
  // li_at cookies are typically 100–300 chars, alphanumeric + a few symbols.
  return trimmed.length >= 20 && /^[A-Za-z0-9_\-+/=]+$/.test(trimmed);
}

export function CookieConnectMethod({ onConnected }: CookieConnectMethodProps): ReactNode {
  const t = useT('accounts');
  const [liAt, setLiAt] = useState('');
  const [stage, setStage] = useState<Stage>('paste');
  const [profile, setProfile] = useState<ValidateCookieResponse['profile'] | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const validate = useValidateCookie();
  const create = useCreateLinkedinAccount();

  const handleValidate = useCallback(async () => {
    setErrorKey(null);
    if (!looksLikeLiAt(liAt)) {
      setErrorKey('connect.cookie.error.invalid_format');
      return;
    }
    try {
      const response = await validate.mutateAsync({ liAt: liAt.trim() });
      setProfile(response.profile);
      setStage('preview');
    } catch (err) {
      setErrorKey(mapApiError(err));
    }
  }, [liAt, validate]);

  const handleConnect = useCallback(async () => {
    if (!profile) return;
    setErrorKey(null);
    try {
      const response = await create.mutateAsync({
        name: profile.name,
        vanitySlug: profile.vanitySlug ?? undefined,
        linkedinUrn: profile.linkedinUrn ?? undefined,
        avatarUrl: profile.avatarUrl ?? undefined,
        voiceLabel: profile.headline || undefined,
        liAt: liAt.trim(),
        source: 'cookie',
      });
      // Wipe the cleartext cookie from React state ASAP.
      setLiAt('');
      setProfile(null);
      setStage('paste');
      onConnected(response.account);
    } catch (err) {
      setErrorKey(mapApiError(err));
    }
  }, [create, liAt, onConnected, profile]);

  const handleCancel = useCallback(() => {
    setStage('paste');
    setProfile(null);
    setErrorKey(null);
  }, []);

  return (
    <Card padding="md" radius="2xl" data-region="connect-cookie" data-method="cookie">
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-dim text-accent"
          >
            <Lock style={{ width: 18, height: 18 }} />
          </span>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-bold text-primary">{t('connect.cookie.title')}</span>
            <span className="text-xs leading-relaxed text-secondary">
              {t('connect.cookie.body')}
            </span>
          </div>
        </div>

        {stage === 'paste' ? (
          <>
            <label className="flex flex-col gap-1.5 text-xs font-semibold text-secondary">
              <span>{t('connect.cookie.textarea_label')}</span>
              <Textarea
                value={liAt}
                onChange={(e) => setLiAt(e.target.value)}
                placeholder={t('connect.cookie.textarea_placeholder')}
                monospace
                rows={3}
                spellCheck={false}
                autoComplete="off"
                data-testid="connect-cookie-input"
                aria-invalid={Boolean(errorKey)}
              />
            </label>
            {errorKey ? (
              <Banner tone="error" data-testid="connect-cookie-error">
                {t(errorKey)}
              </Banner>
            ) : null}
            <p className="text-2xs leading-relaxed text-tertiary">
              <ShieldCheck
                aria-hidden
                style={{ width: 12, height: 12, display: 'inline-block', marginRight: 4 }}
              />
              {t('connect.cookie.security_note')}
            </p>
            <Button
              type="button"
              variant="primary"
              size="md"
              fullWidth
              loading={validate.isPending}
              disabled={liAt.trim().length === 0}
              onClick={() => {
                void handleValidate();
              }}
              data-testid="connect-cookie-validate"
            >
              {validate.isPending
                ? t('connect.cookie.validating')
                : t('connect.cookie.validate_button')}
            </Button>
          </>
        ) : profile ? (
          <CookieConfirmationCard
            profile={profile}
            confirming={create.isPending}
            errorKey={errorKey}
            onConfirm={() => {
              void handleConnect();
            }}
            onCancel={handleCancel}
          />
        ) : null}
      </div>
    </Card>
  );
}

interface CookieConfirmationCardProps {
  profile: ValidateCookieResponse['profile'];
  confirming: boolean;
  errorKey: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function CookieConfirmationCard({
  profile,
  confirming,
  errorKey,
  onConfirm,
  onCancel,
}: CookieConfirmationCardProps): ReactNode {
  const t = useT('accounts');
  const initials = profile.name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const profileUrl = profile.vanitySlug ? `linkedin.com/in/${profile.vanitySlug}` : null;
  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-default bg-bg-elevated p-3"
      data-testid="connect-confirmation-card"
    >
      <Banner tone="success">{t('connect.cookie.valid_match', { name: profile.name })}</Banner>
      <div className="flex items-start gap-3">
        <Avatar
          size="lg"
          src={profile.avatarUrl ?? undefined}
          initials={initials}
          tone="identity"
          identityColor="var(--accent-default)"
          alt={profile.name}
        />
        <div className="flex flex-1 flex-col gap-1 min-w-0">
          <span className="text-sm font-bold text-primary truncate">{profile.name}</span>
          {profile.headline ? (
            <span className="text-xs text-secondary truncate">{profile.headline}</span>
          ) : null}
          {profileUrl ? (
            <span className="text-2xs text-tertiary truncate">{profileUrl}</span>
          ) : null}
        </div>
      </div>
      {errorKey ? <Banner tone="error">{t(errorKey)}</Banner> : null}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Button
          type="button"
          variant="ghost"
          size="md"
          fullWidth
          onClick={onCancel}
          disabled={confirming}
          data-testid="connect-confirmation-cancel"
        >
          {t('connect.confirm.cancelCta')}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth
          loading={confirming}
          onClick={onConfirm}
          data-testid="connect-confirmation-confirm"
        >
          {confirming ? t('connect.confirm.confirming') : t('connect.confirm.confirmCta')}
        </Button>
      </div>
    </div>
  );
}
